import { ThemeContextConsumer } from "../../application/themeContext";
import { alpha } from "@mui/material/styles";
import { query } from "./graph";
import { useQuery } from "@apollo/client";
import { withStyles } from "@mui/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AppBar from "@mui/material/AppBar";
import BrightnessMediumIcon from "@mui/icons-material/BrightnessMedium";
import Button from "@mui/material/Button";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import React, { useCallback, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import config from "../../application/config";

const styles = theme => ({
  appBar: {
    zIndex: theme.zIndex.modal + 1
  },
  backButton: {
    color: theme.palette.getContrastText(
      theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor
    ),
    marginRight: 20,
    marginLeft: -12
  },
  currentViewer: {
    marginLeft: theme.spacing(1)
  },
  menuTextSpacing: {
    marginLeft: theme.spacing(1.5)
  },
  menu: {
    zIndex: theme.zIndex.modal + 1
  },
  title: {
    color: theme.palette.getContrastText(
      theme.components.MuiAppBar.styleOverrides.colorPrimary.backgroundColor
    )
  },
  search: {
    position: "relative",
    borderRadius: theme.shape.borderRadius,
    backgroundColor: alpha(theme.palette.common.white, 0.15),
    "&:hover": {
      backgroundColor: alpha(theme.palette.common.white, 0.25)
    },
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("md")]: {
      marginLeft: theme.spacing(1),
      width: "auto"
    }
  },
  searchIcon: {
    width: theme.spacing(5),
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  },
  inputRoot: {
    color: "inherit",
    width: "100%"
  },
  inputInput: {
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(5),
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      width: 240,
      "&:focus": {
        width: 260
      }
    }
  },
  signOutButton: {
    marginLeft: theme.spacing(1)
  }
});

const ITEM_HEIGHT = 48;

const DesktopAppBar = ({
  classes,
  title,
  onSearch,
  onRequestBack,
  backgroundColor
}) => {
  const { data } = useQuery(query);

  const [searchQuery, setSearchQuery] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleBackButtonClick = useCallback(() => {
    if (onRequestBack) onRequestBack();
  }, [onRequestBack]);

  const handleSearchQueryChange = useCallback(event => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSearchQuerySubmit = useCallback(
    event => {
      event.preventDefault();
      if (onSearch) {
        onSearch(searchQuery);
      }
    },
    [onSearch, searchQuery]
  );

  const handleSignOutButtonClick = useCallback(() => {
    window.location =
      config.getFlynetCoreLogoutUrl() +
      "?client_id=" +
      config.getFlynetCoreClientId() +
      "&return_to=" +
      window.location.origin;
  }, []);

  return (
    <AppBar
      className={classes.appBar}
      style={backgroundColor ? { backgroundColor } : {}}
      position="sticky"
    >
      <Toolbar style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            className={classes.backButton}
            aria-label="Back"
            onClick={handleBackButtonClick}
          >
            <ChevronLeft />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
        </div>

        {onSearch && (
          <div className={classes.search}>
            <form onSubmit={handleSearchQuerySubmit}>
              <div className={classes.searchIcon}>
                <SearchIcon />
              </div>
              <InputBase
                placeholder="Search for Order Id / PO / Customer Name…"
                classes={{ root: classes.inputRoot, input: classes.inputInput }}
                onChange={handleSearchQueryChange}
              />
            </form>
          </div>
        )}

        <IconButton
          aria-haspopup="true"
          aria-owns="app-bar-menu"
          onClick={event => setAnchorEl(event.currentTarget)}
        >
          <AccountCircleIcon />
        </IconButton>

        <Menu
          className={classes.menu}
          id="app-bar-menu"
          anchorEl={anchorEl}
          keepMounted
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{ style: { maxHeight: ITEM_HEIGHT * 4.5, width: 200 } }}
        >
          <MenuItem>
            <AccountCircleIcon />
            <Typography className={classes.currentViewer}>
              {data?.viewer?.me?.name || "User"}
            </Typography>
          </MenuItem>
          <MenuItem>
            <ThemeContextConsumer>
              {context =>
                context &&
                context.componentMounted && (
                  <React.Fragment>
                    <IconButton onClick={context.toggleTheme}>
                      <BrightnessMediumIcon color="action" />
                      {context.type === "light" && (
                        <Typography className={classes.menuTextSpacing}>
                          Toggle Dark Mode
                        </Typography>
                      )}
                      {context.type === "dark" && (
                        <Typography className={classes.menuTextSpacing}>
                          Toggle Light Mode
                        </Typography>
                      )}
                    </IconButton>
                  </React.Fragment>
                )
              }
            </ThemeContextConsumer>
          </MenuItem>
          <MenuItem>
            <Button fullWidth onClick={handleSignOutButtonClick}>
              <Typography align="center">Sign out</Typography>
            </Button>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default withStyles(styles)(DesktopAppBar);
